/**
 * ContentMixView.jsx
 * ──────────────────
 * v2.13.19 (2026-02) — Per-station weekly content-mix breakdown.
 *
 * Backed by GET /api/admin/content-mix/station/{station_id}?week=current|previous
 *
 * Operator brief:
 *   1. Show the % of music / speech / mixed (DJ talkover OR ads) per
 *      hour, normalised so the bar always sums to 100% of the
 *      *evaluable* samples (timeouts excluded but flagged via the
 *      `coverage_pct` column so the operator can spot bad hours).
 *   2. Two presets: current week (Mon-Sun, fills as the week
 *      progresses) and previous week (the last completed Mon-Sun).
 *   3. The "ads-est" column comes from the Q1.b heuristic — runs of
 *      2-4 consecutive speech+music samples (≈ 20-40s) are flagged
 *      as ad-like. Treat it as a directional indicator, not a meter.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Calendar, Music2, Mic, AudioLines, Volume2, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PALETTE = {
  music:  '#4ade80',
  speech: '#fbbf24',
  mixed:  '#60a5fa',
  noise:  '#a78bfa',
  ads:    '#f87171',
};

const Bar = ({ label, value, hex, max = 100 }) => {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="space-y-0.5" title={`${label}: ${pct.toFixed(1)}%`}>
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-white/65">{label}</span>
        <span className="tabular-nums font-semibold" style={{ color: hex }}>
          {pct.toFixed(1)}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${(pct / max) * 100}%`, backgroundColor: hex }}
        />
      </div>
    </div>
  );
};

// v2.13.130 — Monitoring-coverage annotation. Shown OUTSIDE the content
// percentages so the station is never blamed for our own downtime.
const MonitoringCoverageBar = ({ mc }) => {
  const { t, currentLang } = useLanguage();
  if (!mc || mc.status !== 'ok' || mc.coverage_pct == null) return null;
  const locale = currentLang === 'el' ? 'el-GR' : currentLang || 'el-GR';
  const pct = Math.max(0, Math.min(100, mc.coverage_pct));
  const gaps = mc.gaps || [];
  const tz = { timeZone: 'Europe/Athens' };
  const fmtGap = (g) => {
    const s = new Date(g.start_utc);
    const e = new Date(g.end_utc);
    const day = s.toLocaleDateString(locale, { ...tz, day: '2-digit', month: '2-digit' });
    const t1 = s.toLocaleTimeString(locale, { ...tz, hour: '2-digit', minute: '2-digit' });
    const t2 = e.toLocaleTimeString(locale, { ...tz, hour: '2-digit', minute: '2-digit' });
    const cause = g.cause === 'restart'
      ? (t('contentMix_covCauseRestart') || 'επανεκκίνηση συστήματος')
      : (t('contentMix_covCauseOutage') || 'διακοπή παρακολούθησης');
    return `${day} ${t1}–${t2} (${cause})`;
  };
  const totalMin = Math.round((mc.gap_seconds_total || 0) / 60);
  const hUnit = t('contentMix_covH') || 'ω';
  const mUnit = t('contentMix_covM') || 'λ';
  const durText = totalMin >= 60
    ? `${Math.floor(totalMin / 60)}${hUnit} ${totalMin % 60}${mUnit}`
    : `${totalMin}${mUnit}`;
  const shown = gaps.slice(0, 3);
  const more = gaps.length - shown.length;
  return (
    <div
      className="bg-sky-500/[0.07] border border-sky-400/25 rounded-lg p-3.5"
      data-testid="monitoring-coverage-bar"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-sky-300 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          {t('contentMix_covTitle') || 'Κάλυψη παρακολούθησης'}
        </div>
        <div
          className="text-[14px] font-bold tabular-nums text-sky-300"
          data-testid="monitoring-coverage-pct"
        >
          {pct.toFixed(1)}%
        </div>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden flex mb-2">
        <div className="h-full bg-sky-400" style={{ width: `${pct}%` }} />
        <div
          className="h-full"
          style={{
            width: `${100 - pct}%`,
            background:
              'repeating-linear-gradient(45deg,#475569,#475569 4px,#334155 4px,#334155 8px)',
          }}
        />
      </div>
      {gaps.length > 0 ? (
        <div className="text-[11px] text-white/65" data-testid="monitoring-coverage-gaps">
          {t('contentMix_covGapsLabel') || 'Εκτός παρακολούθησης'}:{' '}
          <b className="text-white/85">{durText}</b>
          {' — '}
          {shown.map(fmtGap).join(' · ')}
          {more > 0 ? ` · +${more}` : ''}
        </div>
      ) : (
        <div className="text-[11px] text-white/65" data-testid="monitoring-coverage-nogaps">
          {t('contentMix_covNoGaps') || 'Χωρίς κενά παρακολούθησης σε αυτό το παράθυρο ✓'}
        </div>
      )}
      <div className="text-[10px] text-sky-300/70 italic mt-1.5">
        {t('contentMix_covNote') ||
          'Τα ποσοστά υπολογίζονται μόνο στον χρόνο ενεργής παρακολούθησης — τα κενά ΔΕΝ χρεώνονται στον σταθμό.'}
      </div>
    </div>
  );
};

const ContentMixView = ({ stationId, token }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [week, setWeek] = useState('current');

  const fetchData = useCallback(async () => {
    if (!stationId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/admin/content-mix/station/${stationId}?week=${week}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`HTTP ${r.status}: ${text.slice(0, 200)}`);
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [stationId, token, week]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.weekly_summary || {};
  const breakdown = data?.hourly_breakdown || [];

  return (
    <div className="space-y-4" data-testid="content-mix-view">
      {/* Header — week selector + date range */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md p-0.5">
          <button
            type="button"
            onClick={() => setWeek('current')}
            className={`px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
              week === 'current'
                ? 'bg-amber-500/20 text-amber-300'
                : 'text-white/60 hover:text-white/90'
            }`}
            data-testid="content-mix-week-current-btn"
          >
            {t('contentMix_currentWeek') || 'Τρέχουσα Εβδομάδα'}
          </button>
          <button
            type="button"
            onClick={() => setWeek('previous')}
            className={`px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
              week === 'previous'
                ? 'bg-amber-500/20 text-amber-300'
                : 'text-white/60 hover:text-white/90'
            }`}
            data-testid="content-mix-week-previous-btn"
          >
            {t('contentMix_previousWeek') || 'Προηγούμενη Εβδομάδα'}
          </button>
        </div>
        {data && (
          <div className="inline-flex items-center gap-2 text-[12px] text-white/55">
            <Calendar className="w-3.5 h-3.5 text-white/35" />
            <span className="tabular-nums">{data.from_date}</span>
            <span className="text-white/30">→</span>
            <span className="tabular-nums">{data.to_date}</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 text-white/60 text-sm py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('contentMix_loading') || 'Φόρτωση…'}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {!loading && data && summary.samples_total === 0 && (
        <>
          <div className="bg-white/5 border border-white/10 rounded-lg p-8 text-center text-sm text-white/55">
            {t('contentMix_noSamples') || 'Δεν υπάρχουν διαθέσιμες πληροφορίες'}
          </div>
          <MonitoringCoverageBar mc={data.monitoring_coverage} />
        </>
      )}

      {!loading && data && summary.samples_total > 0 && (
        <>
          {/* Weekly headline metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="content-mix-weekly-summary">
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-white/45 flex items-center gap-1">
                <Music2 className="w-3 h-3" /> {t('contentMix_music') || 'Μουσική'}
              </div>
              <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: PALETTE.music }}>
                {summary.music_pct.toFixed(1)}%
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-white/45 flex items-center gap-1">
                <Mic className="w-3 h-3" /> {t('contentMix_speech') || 'Ομιλία'}
              </div>
              <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: PALETTE.speech }}>
                {summary.speech_pct.toFixed(1)}%
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-white/45 flex items-center gap-1">
                <AudioLines className="w-3 h-3" /> {t('contentMix_mixed') || 'Μεικτό (DJ/Διαφ.)'}
              </div>
              <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: PALETTE.mixed }}>
                {summary.mixed_pct.toFixed(1)}%
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3" title={t('contentMix_ads_hint') || 'Heuristic: consecutive speech+music runs of 20-40s'}>
              <div className="text-[10px] uppercase tracking-wide text-white/45 flex items-center gap-1">
                <Volume2 className="w-3 h-3" /> {t('contentMix_adsEstimate') || 'Διαφ. (εκτίμηση)'}
              </div>
              <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: PALETTE.ads }}>
                {summary.ads_estimate_pct.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* v2.13.130 — monitoring coverage annotation (outside percentages) */}
          <MonitoringCoverageBar mc={data.monitoring_coverage} />

          {/* Hourly breakdown table */}
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 bg-white/5 border-b border-white/10">
              <h4 className="text-[12px] font-bold uppercase tracking-wide text-white/85">
                {t('contentMix_hourlyTable') || 'Ανά ώρα'}
              </h4>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/50 sticky top-0">
                  <tr className="text-[10px] text-white/45 uppercase">
                    <th className="text-left p-2 pl-4">Ώρα</th>
                    <th className="text-right p-2 text-emerald-400">Music</th>
                    <th className="text-right p-2 text-amber-400">Speech</th>
                    <th className="text-right p-2 text-blue-400">Mixed</th>
                    <th className="text-right p-2 pr-4 text-red-400">Διαφ.~</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((b) => (
                    <tr
                      key={`${b.date}-${b.hour}`}
                      className="border-t border-white/5 hover:bg-white/[0.03]"
                      data-testid={`content-mix-row-${b.date}-${b.hour}`}
                    >
                      <td className="p-2 pl-4 tabular-nums text-white/85">{b.label}</td>
                      <td className="text-right p-2 tabular-nums text-emerald-300">{b.music_pct.toFixed(1)}%</td>
                      <td className="text-right p-2 tabular-nums text-amber-300">{b.speech_pct.toFixed(1)}%</td>
                      <td className="text-right p-2 tabular-nums text-blue-300">{b.mixed_pct.toFixed(1)}%</td>
                      <td className="text-right p-2 pr-4 tabular-nums text-red-300">{b.ads_estimate_pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="text-[10px] text-white/40 italic">
            {t('contentMix_disclaimer') ||
              '* Mixed = ομιλία πάνω σε μουσικό χαλί (DJ talkover ή διαφημίσεις). Η εκτίμηση διαφημίσεων (~) βασίζεται σε runs 20-40s — directional, όχι ακριβής μέτρηση.'}
          </div>
        </>
      )}
    </div>
  );
};

export default ContentMixView;
